"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getLocaleFromHostHeader = exports.getLocaleFromTLD = exports.getTLDLocale = exports.extractTLD = exports.extractHostTLD = void 0;
const webapp_constants_1 = require("@eventbrite/webapp-constants");
const constants_1 = require("./constants");
const utils_1 = require("./utils");
/**
 * Extracts the top-level domain (TLD) from a given host string.
 *
 * @param host - The host string (e.g., "eventbrite.co.uk").
 * @returns The TLD (e.g., "co.uk") if the host matches the expected pattern, otherwise null.
 *
 * The function uses a regex to match specific subdomains (`evbqa`, `evbstage`, `eventbrite`)
 * and extracts the portion of the domain after the dot (e.g., ".co.uk").
 */
const extractHostTLD = (host) => {
    if (!host) {
        return null;
    }
    const tld = (host.match(/(?:evbqa|evbstage|eventbrite)(\.\S+)$/) || [])[1];
    return tld ? tld.slice(1) : null;
};
exports.extractHostTLD = extractHostTLD;
/**
 * Extracts the TLD from the `eb-host` header in the incoming HTTP request.
 *
 * @param request - The incoming HTTP request object.
 * @returns The TLD (e.g., "co.uk") if the `eb-host` header is present and valid, otherwise null.
 *
 * Because the BFF will likely be exposed behind Cloudfront / APIGateway
 * we cannot rely on the host, and instead extract the eb tld from eb-host header.
 */
const extractTLD = (request) => {
    const forwardedHost = request.headers[webapp_constants_1.EventbriteHeader.EB_HOST];
    return (0, exports.extractHostTLD)(forwardedHost);
};
exports.extractTLD = extractTLD;
/**
 * Maps a TLD to its corresponding locale.
 *
 * @param tld - The TLD string (e.g., "co.uk").
 * @returns The locale associated with the TLD (e.g., "en-GB") if supported, otherwise null.
 *
 * This function uses the `SUPPORTED_TLDS` constant to check if the TLD is valid and maps it
 * to its parent locale using the `localeParentMap`.
 */
const getTLDLocale = (tld) => {
    const tldLocale = constants_1.SUPPORTED_TLDS[tld];
    return tldLocale ? utils_1.localeParentMap[tldLocale] : null;
};
exports.getTLDLocale = getTLDLocale;
/**
 * Extracts the locale from the TLD in the incoming HTTP request.
 *
 * @param request - The incoming HTTP request object.
 * @returns The locale (e.g., "en-GB") if the TLD is valid and supported, otherwise null.
 *
 * This function combines `extractTLD` and `getTLDLocale` to determine the locale
 * based on the TLD in the `eb-host` header.
 */
const getLocaleFromTLD = (request) => {
    const tld = (0, exports.extractTLD)(request);
    const tldLocale = tld ? (0, exports.getTLDLocale)(tld) : null;
    return tldLocale;
};
exports.getLocaleFromTLD = getLocaleFromTLD;
/**
 * Extracts the locale from the host header.
 *
 * @param hostHeader - The host header string (e.g., "eventbrite.co.uk").
 * @returns The locale (e.g., "en-GB") if the TLD is valid and supported, otherwise null.
 *
 * This function is similar to `getLocaleFromTLD` but works directly with a host header string
 * instead of an HTTP request object.
 */
const getLocaleFromHostHeader = (hostHeader) => {
    const tld = (0, exports.extractHostTLD)(hostHeader);
    const tldLocale = tld ? (0, exports.getTLDLocale)(tld) : null;
    return tldLocale;
};
exports.getLocaleFromHostHeader = getLocaleFromHostHeader;
//# sourceMappingURL=tld.js.map